import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useSetAtom } from 'jotai';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { CustomOperatorPoliciesIDs, FeatureFlagUseCase } from '@ibk/shared-types';
import { Button, Loading } from '@ibk/shared-ui';
import { operatorDrawerErrorAtom, operatorDrawerNavigationAtom, operatorDrawerSuccessAtom } from '@ibk/shared-utils';
import { sharedLayoutTranslations } from '../../../../translations';
import { useGetOperatorPermissionsQuery, useUpdateOperatorPermissionsMutation } from '../../services/operatorsApi';
import { CustomOperatorDetails } from '../RegisterType/components';
import styles from './EditOperator.module.css';
const isValidTransactionLimit = (value) => value >= 0.01 && value <= 999999999.99 && Math.round(value * 100) / 100 === value;
const selectedTransactionLimit = (limit) => {
    if (limit?.kind === 'on')
        return limit.value;
    if (limit?.kind === 'off')
        return null;
    return undefined;
};
const EditOperatorBase = ({ currentRoute, handleGoBack }) => {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed, isLoading: isLoadingFeatureFlags, error: featureFlagError } = useFeatureFlag();
    const isNfseOperatorPermissionEnabled = !isLoadingFeatureFlags &&
        !featureFlagError &&
        isFeatureFlagWhiteListed(FeatureFlagUseCase.MODULE_BILLING_NFSE_STANDALONE);
    const partyId = currentRoute.params?.operatorPartyId ?? '';
    const { data, isFetching, isError } = useGetOperatorPermissionsQuery(partyId, {
        skip: !partyId,
        refetchOnMountOrArgChange: true,
    });
    const [updateOperator, { isLoading: isSaving }] = useUpdateOperatorPermissionsMutation();
    const setError = useSetAtom(operatorDrawerErrorAtom);
    const setSuccess = useSetAtom(operatorDrawerSuccessAtom);
    const navigate = useSetAtom(operatorDrawerNavigationAtom);
    const [form, setForm] = React.useState(null);
    React.useEffect(() => {
        if (data) {
            setForm({
                accountManager: data.accountManager,
                billingManager: data.billingManager,
                benefitsManager: data.benefitsManager,
                nfseManager: data.nfseManager ?? false,
                limit: data.transactionLimit === null ? { kind: 'off' } : { kind: 'on', value: data.transactionLimit },
            });
        }
    }, [data]);
    const updatePermission = (field, value) => {
        setForm(current => {
            if (!current)
                return current;
            if (field === 'accountManager' && value === false) {
                return { ...current, accountManager: false, limit: { kind: 'off' } };
            }
            return { ...current, [field]: value };
        });
    };
    const toggleLimit = () => {
        setForm(current => {
            if (!current)
                return current;
            return { ...current, limit: current.limit.kind === 'off' ? { kind: 'editing' } : { kind: 'off' } };
        });
    };
    const updateLimit = (value) => {
        setForm(current => {
            if (!current)
                return current;
            return {
                ...current,
                limit: typeof value === 'number' && isValidTransactionLimit(value)
                    ? { kind: 'on', value }
                    : { kind: 'editing', value },
            };
        });
    };
    const isValidLimit = form?.limit.kind !== 'editing';
    const selectedLimit = selectedTransactionLimit(form?.limit);
    const hasChanges = Boolean(data &&
        form &&
        (data.accountManager !== form.accountManager ||
            data.billingManager !== form.billingManager ||
            data.benefitsManager !== form.benefitsManager ||
            (isNfseOperatorPermissionEnabled && data.nfseManager !== form.nfseManager) ||
            data.transactionLimit !== selectedLimit));
    const canSave = Boolean(form && data && hasChanges && isValidLimit && !isSaving);
    const handleSave = async () => {
        if (!canSave || !form || !partyId || form.limit.kind === 'editing')
            return;
        const payload = {
            accountManager: form.accountManager,
            billingManager: form.billingManager,
            benefitsManager: form.benefitsManager,
            ...(isNfseOperatorPermissionEnabled ? { nfseManager: form.nfseManager } : {}),
            transactionLimit: form.limit.kind === 'on' ? form.limit.value : null,
        };
        try {
            await updateOperator({ partyId, payload }).unwrap();
            setSuccess(t('operator.drawer.pages.edit.save-success'));
            navigate('clear');
        }
        catch {
            setError(t('operator.drawer.pages.edit.save-error'));
        }
    };
    if (!partyId) {
        return (_jsx("div", { className: styles.loading, "data-testid": 'operator-edit-load-error', children: t('operator.drawer.pages.edit.load-error') }));
    }
    if (isFetching || (!form && !isError)) {
        return (_jsx("div", { className: styles.loading, "data-testid": 'operator-edit-loading', children: _jsx(Loading, {}) }));
    }
    if (isError || !form || !data) {
        return (_jsx("div", { className: styles.loading, "data-testid": 'operator-edit-load-error', children: t('operator.drawer.pages.edit.load-error') }));
    }
    const restrictedPolicies = [
        !form.accountManager && CustomOperatorPoliciesIDs.ACCOUNT_MANAGER,
        form.limit.kind === 'off' && CustomOperatorPoliciesIDs.TRANSACTION_LIMIT,
        !form.billingManager && CustomOperatorPoliciesIDs.BILLING_MANAGER,
        isNfseOperatorPermissionEnabled && !form.nfseManager && CustomOperatorPoliciesIDs.NFSE_MANAGER,
        !form.benefitsManager && CustomOperatorPoliciesIDs.BENEFITS_MANAGER,
    ].filter((policy) => Boolean(policy));
    const items = [
        {
            id: CustomOperatorPoliciesIDs.ACCOUNT_MANAGER,
            title: t('operator.drawer.pages.edit.account-title'),
            text: t('operator.drawer.pages.register-type.operator.pj-account.description'),
            restrictedPolicies: [],
        },
        {
            id: CustomOperatorPoliciesIDs.TRANSACTION_LIMIT,
            title: t('operator.drawer.pages.register-type.operator.transaction-limit.title'),
            text: t('operator.drawer.pages.register-type.operator.transaction-limit.description'),
            restrictedPolicies: [],
            preRequisite: CustomOperatorPoliciesIDs.ACCOUNT_MANAGER,
            customValueFunction: (_, value) => updateLimit(value),
            customValueError: !isValidLimit,
            customValueErrorMessage: t('operator.drawer.pages.edit.invalid-limit'),
        },
        {
            id: CustomOperatorPoliciesIDs.BILLING_MANAGER,
            title: t('operator.drawer.pages.register-type.operator.billing.title'),
            text: t('operator.drawer.pages.register-type.operator.billing.description'),
            restrictedPolicies: [],
        },
        ...(isNfseOperatorPermissionEnabled
            ? [
                {
                    id: CustomOperatorPoliciesIDs.NFSE_MANAGER,
                    title: t('operator.drawer.pages.register-type.operator.nfse.title'),
                    text: t('operator.drawer.pages.register-type.operator.nfse.description'),
                    restrictedPolicies: [],
                },
            ]
            : []),
        {
            id: CustomOperatorPoliciesIDs.BENEFITS_MANAGER,
            title: t('operator.drawer.pages.register-type.operator.benefits.title'),
            text: t('operator.drawer.pages.register-type.operator.benefits.description'),
            restrictedPolicies: [],
        },
    ];
    const togglePolicy = (policy) => {
        switch (policy) {
            case CustomOperatorPoliciesIDs.ACCOUNT_MANAGER:
                updatePermission('accountManager', !form.accountManager);
                break;
            case CustomOperatorPoliciesIDs.TRANSACTION_LIMIT:
                toggleLimit();
                break;
            case CustomOperatorPoliciesIDs.BILLING_MANAGER:
                updatePermission('billingManager', !form.billingManager);
                break;
            case CustomOperatorPoliciesIDs.NFSE_MANAGER:
                updatePermission('nfseManager', !form.nfseManager);
                break;
            case CustomOperatorPoliciesIDs.BENEFITS_MANAGER:
                updatePermission('benefitsManager', !form.benefitsManager);
                break;
        }
    };
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.content, children: [_jsx("p", { className: styles.description, children: t('operator.drawer.pages.edit.description') }), _jsxs("div", { className: styles.card, children: [_jsx("h3", { className: styles.cardTitle, children: t('operator.drawer.pages.register-type.operator.title') }), _jsx(CustomOperatorDetails, { description: t('operator.drawer.pages.register-type.operator.description'), items: items, dataTestid: 'operator-edit', restrictedPolicies: restrictedPolicies, customValues: {
                                    [CustomOperatorPoliciesIDs.TRANSACTION_LIMIT]: form.limit.kind === 'off' ? undefined : form.limit.value,
                                }, checkPolicy: togglePolicy, isEditing: true, isSaving: isSaving })] })] }), _jsxs("div", { className: styles.footer, children: [_jsx(Button, { variant: 'secondary', size: 'medium', dataTestId: 'operator-edit-cancel', onClick: handleGoBack, disabled: isSaving, children: t('operator.drawer.pages.edit.cancel') }), _jsx(Button, { variant: 'primary', size: 'medium', dataTestId: 'operator-edit-save', onClick: () => {
                            void handleSave();
                        }, disabled: !canSave, children: t('operator.drawer.pages.edit.save') })] })] }));
};
const EditOperator = (props) => (_jsx(TranslationProvider, { translations: sharedLayoutTranslations, defaultLanguage: 'pt-BR', children: _jsx(EditOperatorBase, { ...props }) }));
export default EditOperator;
